import { useSearchParams, Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { MEMBERSHIP_CATEGORIES } from "@/lib/brand";
import { Building2, MapPinned, Flag, Briefcase, School, Users, ArrowRight, CheckCircle2 } from "lucide-react";

const ICONS = { Building2, MapPinned, Flag, Briefcase, School, Users };

const PRICING = {
  independent_org: ["Verified entry in national registry", "Federation logo usage rights", "Event hosting eligibility"],
  state: ["State-level governance privileges", "District approval authority", "Annual conference participation"],
  zonal_national: ["Zonal championship hosting", "National calendar inclusion", "Three-discipline participation"],
  corporate: ["CSR & sponsorship recognition", "Co-branded national programmes", "Federation partner benefits"],
  academy_club: ["Coach & athlete onboarding", "Academy ranking points", "Federation seal & national listing"],
  active_youth: ["Youth ID card", "Internship & volunteer access", "Mentorship from senior athletes"],
};

export default function Memberships() {
  const [params] = useSearchParams();
  const selected = params.get("cat");

  return (
    <div data-testid="memberships-page">
      <PageHero
        eyebrow="Memberships"
        title="Six Categories. One Federation."
        subtitle="Choose the category that best represents you and become an officially registered stakeholder of the National Karate Federation – NKF Bharat."
      />

      <section className="section-spacing nkf-container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {MEMBERSHIP_CATEGORIES.map((c, i) => {
            const Icon = ICONS[c.icon] || Building2;
            const isActive = selected === c.key;
            return (
              <div
                key={c.key}
                data-testid={`membership-detail-${c.key}`}
                className={`federation-card rounded-md p-6 border flex flex-col h-full ${isActive ? "border-nkf-gold ring-4 ring-nkf-gold/30 bg-nkf-blue text-white" : "border-slate-200 bg-white"}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`h-11 w-11 rounded-md ${isActive ? "bg-white/10 border border-white/15" : "bg-nkf-blue/5 border border-nkf-blue/15"} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${isActive ? "text-nkf-gold" : "text-nkf-blue"}`} />
                  </div>
                  <div className={`text-[11px] uppercase tracking-widest ${isActive ? "text-white/70" : "text-slate-400"}`}>0{i + 1}</div>
                </div>
                <h3 className={`font-display text-xl mt-5 min-h-[3.5rem] ${isActive ? "text-white" : "text-nkf-blue"}`}>{c.title}</h3>
                <ul className={`mt-4 space-y-2 text-sm ${isActive ? "text-white/85" : "text-slate-600"}`}>
                  {c.items.map((it) => (
                    <li key={it} className="flex items-center gap-2">
                      <span className={`h-1 w-1 rounded-full ${isActive ? "bg-nkf-gold" : "bg-nkf-red"}`} /> {it}
                    </li>
                  ))}
                </ul>
                <div className={`mt-5 pt-5 border-t ${isActive ? "border-white/15" : "border-slate-100"} text-sm flex-1`}>
                  <div className={`text-[11px] uppercase tracking-widest ${isActive ? "text-nkf-gold" : "text-nkf-red"} font-semibold mb-2`}>Benefits</div>
                  <ul className="space-y-1.5">
                    {(PRICING[c.key] || []).map((b) => (
                      <li key={b} className={`flex items-start gap-2 ${isActive ? "text-white/85" : "text-slate-600"}`}>
                        <CheckCircle2 className={`h-3.5 w-3.5 mt-0.5 ${isActive ? "text-nkf-gold" : "text-nkf-blue"}`} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to={`/register?cat=${c.key}`}
                  data-testid={`apply-${c.key}`}
                  className={`mt-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm uppercase tracking-widest font-semibold transition w-full ${isActive ? "bg-nkf-gold text-nkf-navy hover:bg-white" : "bg-nkf-blue text-white hover:bg-nkf-navy"}`}
                >
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-12 p-6 md:p-8 bg-slate-50 border border-slate-200 rounded-md">
          <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-red font-semibold">Note</div>
          <p className="text-slate-700 mt-2 max-w-3xl">
            Payment of membership and affiliation fees is currently processed offline via the federation office. After submitting your application, our secretariat will contact you with payment instructions. For urgent matters, call the federation helpdesk listed in the footer.
          </p>
        </div>
      </section>
    </div>
  );
}
